import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { getStaffBriefing, Briefing } from "../../../api/staff-agent"

/**
 * "What's new for you" block in the assistant's empty state. Every item is a
 * hook: the insight opens a curiosity gap, the chip seeds a chat
 * investigation (sent verbatim as a message), and the optional link opens
 * the relevant admin page. Renders nothing while loading or on failure —
 * the static greeting copy underneath always stands on its own.
 */
interface BriefingGreetingProps {
  onInvestigate: (message: string) => void
}

const BriefingGreeting: FC<BriefingGreetingProps> = ({ onInvestigate }) => {
  const navigate = useNavigate()
  const [briefing, setBriefing] = useState<Briefing | null>(null)

  useEffect(() => {
    let mounted = true
    getStaffBriefing()
      .then(b => {
        if (mounted) setBriefing(b)
      })
      .catch(() => {
        /* greeting falls back to the static copy */
      })
    return () => {
      mounted = false
    }
  }, [])

  if (!briefing?.items?.length) return null

  return (
    <div className="w-full max-w-xl rounded-xl border border-violet-100 bg-violet-50/60 p-3 text-left">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
        What&apos;s new for you
      </p>
      <ul className="space-y-2.5">
        {briefing.items.map(item => (
          <li key={item.insight} className="flex flex-col gap-1.5">
            <span className="text-sm text-neutral-700">{item.insight}</span>
            <span className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onInvestigate(item.investigateChip)}
                className="rounded-full border border-violet-200 bg-white px-3 py-1 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-100"
              >
                {item.investigateChip}
              </button>
              {item.platformLink && (
                <button
                  type="button"
                  onClick={() => navigate(item.platformLink!)}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-neutral-500 transition-colors hover:text-violet-700"
                >
                  Open
                  <ArrowRight size={12} />
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default BriefingGreeting
